import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowLeft, faEye, faEdit, faTrash, faCheck, faTimes, faClock, faUser, 
    faCalendar, faMapMarkerAlt, faPhone, faEnvelope, faFileAlt, faComments,
    faHeartbeat, faStethoscope, faBrain, faTooth, faBaby, faMicroscope, 
    faDumbbell, faFemale, faEye as faEyeIcon, faTint, faAppleAlt, faProcedures, 
    faAmbulance, faCalendarCheck, faFileMedical, faUserMd, faFileContract, faHandshake,
    faUsers, faShieldAlt, faMoneyBillWave, faClipboardList, faCheckCircle, faTimesCircle,
    faExclamationTriangle, faInfoCircle, faFileUpload, faDownload, faShare, faLock,
    faUnlock, faUserCheck, faUserTimes, faClock as faClockIcon, faCalendarAlt,
    faFileSignature, faHandshake as faHandshakeIcon, faBuilding, faHospital,
    faNotesMedical, faPrescriptionBottle, faXRay, faStethoscope as faStethoscopeIcon
} from '@fortawesome/free-solid-svg-icons';
import { useBaseUrl } from "../../../utils/utils";
import localForage from "localforage";

interface ServiceRequest {
    id: number;
    title: string;
    description: string;
    status: string;
    priority: string;
    engagement_type: string;
    request_type: string;
    
    // Patient and clinic information
    patient_id?: number;
    patient_name?: string;
    patient_phone?: string;
    patient_email?: string;
    creator_id?: number;
    creator_name?: string;
    creator_address?: string;
    creator_phone?: string;
    
    // Contract information
    service_contract_id?: number;
    service_contract_name?: string;
    contract_duration?: string;
    contract_term?: string;
    renewable?: boolean;
    
    // Required services
    required_services?: Array<{
        id: number;
        name: string;
        category: string;
        title: string;
        subtitle: string;
    }>;
    
    // Timing and priority
    start_date?: string;
    end_date?: string;
    response_time: number;
    
    // Pay mode and pricing
    pay_mode: string;
    min_pay?: number;
    max_pay?: number;
    
    // Patient consent and information sharing
    patient_consent_given?: boolean;
    patient_consent_date?: string;
    patient_consent_notes?: string;
    share_patient_demographics?: boolean;
    share_medical_history?: boolean;
    share_current_medications?: boolean;
    share_lab_results?: boolean;
    share_imaging?: boolean;
    share_notes?: boolean;
    
    // Direct partners (if request_type is 'direct')
    direct_partners?: Array<{
        id: number;
        name: string;
        address: string;
    }>;
    
    // Documents
    service_request_documents?: Array<{
        id: number;
        file: string;
        file_description: string;
        created: string;
    }>;
    
    // Applications
    applications?: Array<{
        id: number;
        partner_applying_name: string;
        status: string;
        partner_minimum: number;
        partner_maximum: number;
        pay_mode: string;
        patient_flow: number;
        patient_flow_rate: string;
        partner_qualifications: string;
        partner_experience_years: number;
        partner_certifications: string;
        partner_equipment: string;
        created: string;
        final_rate?: number;
        final_pay_mode?: string;
        final_patient_flow?: number;
        final_patient_flow_rate?: string;
        patient_approved?: boolean;
        patient_notes?: string;
    }>;
    
    // System fields
    created: string;
    updated: string;
    deleted: boolean;
    upgraded_to_collaboration?: number;
}

interface Negotiation {
    id: number;
    application_id: number;
    sender_name: string;
    recipient_name: string;
    message: string;
    message_type: string;
    proposed_rate?: number;
    proposed_pay_mode?: string;
    proposed_patient_flow?: number;
    proposed_patient_flow_rate?: string;
    created: string;
    is_read: boolean;
}

const ServiceRequestDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const baseUrl = useBaseUrl();
    const [serviceRequest, setServiceRequest] = useState<ServiceRequest | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [userRole, setUserRole] = useState<'creator' | 'partner' | 'patient' | null>(null);
    
    // Negotiation state
    const [showNegotiationModal, setShowNegotiationModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<any>(null);
    const [negotiationMessage, setNegotiationMessage] = useState('');
    const [negotiationType, setNegotiationType] = useState('proposal');
    const [proposedTerms, setProposedTerms] = useState({
        rate: '',
        pay_mode: '',
        patient_flow: '',
        patient_flow_rate: ''
    });
    
    // Consent tracking
    const [consentCheckboxes, setConsentCheckboxes] = useState({
        demographics: false,
        medical_history: false,
        medications: false,
        lab_results: false,
        imaging: false,
        notes: false
    });
    const [allSegmentsChecked, setAllSegmentsChecked] = useState(false);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [showInvitePartnersModal, setShowInvitePartnersModal] = useState(false);
    const [showAcceptTermsModal, setShowAcceptTermsModal] = useState(false);
    const [showContractBuilderModal, setShowContractBuilderModal] = useState(false);
    const [showUploadContractFileModal, setShowUploadContractFileModal] = useState(false);
    const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
    const [applyFormData, setApplyFormData] = useState({
        proposed_rate: '',
        proposed_timeline: '',
        proposed_terms: '',
        collaboration_details: '',
        counter_offer: ''
    });

    // Helper function to safely format dates
    const formatDate = (dateString: string | undefined): string => {
        if (!dateString) return 'Not specified';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Invalid date format';
            }
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Date parsing error';
        }
    };

    // Helper function to check if date is valid
    const isValidDate = (dateString: string | undefined): boolean => {
        if (!dateString) return false;
        try {
            const date = new Date(dateString);
            return !isNaN(date.getTime());
        } catch (error) {
            return false;
        }
    };

    useEffect(() => {
        // Get clinic ID and user role from localStorage
        const checkLocalForage = async () => {
            const clinicId = await localForage.getItem("clinic_id");
            const userId = await localForage.getItem("user_id");
            
            setClinicId(clinicId as number);
        };
        
        checkLocalForage();
    }, []);

    useEffect(() => {
        if (id && clinicId) {
            fetchServiceRequestDetails(parseInt(id));
        }
    }, [id, clinicId]);

    const fetchServiceRequestDetails = async (requestId: number) => {
        try {
            setLoading(true);
            setError(null);
            
            // Get the specific service request using URL path parameter
            const response = await fetch(`${baseUrl}collaborations/service-requests/${requestId}/`);
            
            if (response.ok) {
                const data = await response.json();
                const foundRequest = data.data;
                
                if (foundRequest) {
                    setServiceRequest(foundRequest);
                    
                    // Determine user role based on whether they are the creator
                    if (foundRequest.creator_id === clinicId) {
                        setUserRole('creator');
                    } else {
                        setUserRole('partner');
                    }
                    
                    // Set consent checkboxes based on service request settings
                    if (foundRequest.share_patient_demographics) setConsentCheckboxes(prev => ({ ...prev, demographics: true }));
                    if (foundRequest.share_medical_history) setConsentCheckboxes(prev => ({ ...prev, medical_history: true }));
                    if (foundRequest.share_current_medications) setConsentCheckboxes(prev => ({ ...prev, medications: true }));
                    if (foundRequest.share_lab_results) setConsentCheckboxes(prev => ({ ...prev, lab_results: true }));
                    if (foundRequest.share_imaging) setConsentCheckboxes(prev => ({ ...prev, imaging: true }));
                    if (foundRequest.share_notes) setConsentCheckboxes(prev => ({ ...prev, notes: true }));
                } else {
                    setError('Service request not found');
                }
            } else {
                setError('Failed to fetch service request details');
            }
        } catch (error) {
            console.error('Error fetching service request details:', error);
            setError('Error loading service request details');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get icon for service request
    const getRequestIcon = (title: string, engagementType: string) => {
        const lowerTitle = title.toLowerCase();
        
        if (lowerTitle.includes('cardio') || lowerTitle.includes('heart')) return faHeartbeat;
        if (lowerTitle.includes('consultation') || lowerTitle.includes('checkup')) return faStethoscope;
        if (lowerTitle.includes('neuro') || lowerTitle.includes('brain')) return faBrain;
        if (lowerTitle.includes('dental') || lowerTitle.includes('tooth')) return faTooth;
        if (lowerTitle.includes('pediatric') || lowerTitle.includes('child')) return faBaby;
        if (lowerTitle.includes('lab') || lowerTitle.includes('test')) return faMicroscope;
        if (lowerTitle.includes('physio') || lowerTitle.includes('therapy')) return faDumbbell;
        if (lowerTitle.includes('gyneco') || lowerTitle.includes('women')) return faFemale;
        if (lowerTitle.includes('ophthal') || lowerTitle.includes('eye')) return faEyeIcon;
        if (lowerTitle.includes('hematol') || lowerTitle.includes('blood')) return faTint;
        if (lowerTitle.includes('nutrition') || lowerTitle.includes('diet')) return faAppleAlt;
        if (lowerTitle.includes('surgery') || lowerTitle.includes('operation')) return faProcedures;
        if (lowerTitle.includes('emergency') || lowerTitle.includes('urgent')) return faAmbulance;
        if (lowerTitle.includes('appointment') || lowerTitle.includes('schedule')) return faCalendarCheck;
        if (lowerTitle.includes('record') || lowerTitle.includes('report')) return faFileMedical;
        
        return faUserMd; // Default icon
    };

    // Helper function to get status color
    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'draft': return 'secondary';
            case 'pending': return 'warning';
            case 'reviewing': return 'info';
            case 'negotiating': return 'primary';
            case 'patient_approval': return 'info';
            case 'agreed': return 'success';
            case 'rejected': return 'danger';
            case 'cancelled': return 'danger';
            case 'upgraded': return 'success';
            default: return 'secondary';
        }
    };

    // Helper function to get priority color
    const getPriorityColor = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'low': return 'success';
            case 'normal': return 'info';
            case 'high': return 'warning';
            case 'urgent': return 'danger';
            default: return 'secondary';
        }
    };

    // Helper function to get pay mode display
    const getPayModeDisplay = (payMode: string) => {
        switch (payMode) {
            case 'person': return 'Per Person';
            case 'request': return 'Per Request';
            case 'milestone': return 'Per Milestone';
            default: return payMode;
        }
    };

    // Helper function to get patient flow display
    const getPatientFlowDisplay = (flow: number, rate: string) => {
        if (!flow || !rate) return 'Not specified';
        return `${flow} patients per ${rate}`;
    };

    // Handle consent checkbox changes
    const handleConsentChange = (field: string, checked: boolean) => {
        setConsentCheckboxes(prev => ({
            ...prev,
            [field]: checked
        }));
        
        // Check if all segments are checked
        const newCheckboxes = { ...consentCheckboxes, [field]: checked };
        const allChecked = Object.values(newCheckboxes).every(val => val);
        setAllSegmentsChecked(allChecked);
    };

    const handleApplyToRequest = async () => {
        if (!serviceRequest) return;
        
        try {
            // Validate form data
            if (!applyFormData.proposed_rate || !applyFormData.proposed_timeline || !applyFormData.proposed_terms) {
                alert('Please fill in all required fields');
                return;
            }

            // Build minimal payload expected by backend (form-data)
            const formData = new FormData();
            formData.append('service_request', String(serviceRequest.id));
            // Consolidate details into the application note for now
            const note = `Proposed rate: ${applyFormData.proposed_rate}\nTimeline: ${applyFormData.proposed_timeline}\nTerms: ${applyFormData.proposed_terms}\nDetails: ${applyFormData.collaboration_details || ''}\nCounter offer: ${applyFormData.counter_offer || ''}`;
            formData.append('application_note', note);

            // Submit application to existing endpoint
            const response = await fetch(`${baseUrl}collaborations/applications/`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                alert('Application submitted successfully!');
                setShowApplyModal(false);
                // Refresh the page or update state
                window.location.reload();
            } else {
                const errorData = await response.json();
                if (errorData.message === "Cannot apply to an expired service request.") {
                    alert('This service request has expired and is no longer accepting applications.');
                } else {
                    alert(`Failed to submit application: ${errorData.message || 'Unknown error'}`);
                }
            }
        } catch (error) {
            console.error('Error submitting application:', error);
            alert('Error submitting application. Please try again.');
        }
    };



    // Handle counter offer submission
    const handleCounterOffer = async () => {
        if (!selectedApplication || !negotiationMessage.trim()) return;
        
        try {
            const response = await fetch(`${baseUrl}collaborations/negotiations/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    application_id: selectedApplication.id,
                    message: negotiationMessage,
                    message_type: 'counter_offer',
                    proposed_rate: parseInt(proposedTerms.rate) || null,
                    proposed_pay_mode: proposedTerms.pay_mode || null,
                    proposed_patient_flow: parseInt(proposedTerms.patient_flow) || null,
                    proposed_patient_flow_rate: proposedTerms.patient_flow_rate || null,
                    sender_id: clinicId,
                    recipient_id: serviceRequest?.creator_id
                })
            });
            
            if (response.ok) {
                // Update application status to negotiating
                await fetch(`${baseUrl}collaborations/applications/${selectedApplication.id}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        status: 'negotiating'
                    })
                });
                
                setShowNegotiationModal(false);
                setSelectedApplication(null);
                setNegotiationMessage('');
                setProposedTerms({ rate: '', pay_mode: '', patient_flow: '', patient_flow_rate: '' });
                
                // Refresh the service request details
                if (id) {
                    fetchServiceRequestDetails(parseInt(id));
                }
            }
        } catch (error) {
            console.error('Error submitting counter offer:', error);
        }
    };

    // Handle application acceptance
    const handleAcceptApplication = async (applicationId: number) => {
        try {
            const response = await fetch(`${baseUrl}collaborations/applications/${applicationId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: 'accepted'
                })
            });
            
            if (response.ok) {
                // Refresh the service request details
                if (id) {
                    fetchServiceRequestDetails(parseInt(id));
                }
            }
        } catch (error) {
            console.error('Error accepting application:', error);
        }
    };

    // Handle application rejection
    const handleRejectApplication = async (applicationId: number) => {
        try {
            const response = await fetch(`${baseUrl}collaborations/applications/${applicationId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: 'rejected'
                })
            });
            
            if (response.ok) {
                // Refresh the service request details
                if (id) {
                    fetchServiceRequestDetails(parseInt(id));
                }
            }
        } catch (error) {
            console.error('Error rejecting application:', error);
        }
    };

    // Handle conversion to collaboration
    const handleConvertToCollaboration = async (applicationId: number) => {
        try {
            const response = await fetch(`${baseUrl}collaborations/create-collaboration/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    service_request_id: serviceRequest?.id,
                    application_id: applicationId,
                    creating_partner_id: clinicId
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                // Navigate to the new collaboration
                navigate(`/institution/collaborations/${data.collaboration_id}`);
            }
        } catch (error) {
            console.error('Error converting to collaboration:', error);
        }
    };

    if (loading) {
        return (
            <>
                <DashboardHeader />
                <SideMenu />
                <div className={"vertical-overlay"} />
                <div className={"main-content"}>
                    <div className={"page-content"}>
                        <div className={"container-fluid"}>
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading service request details...</p>
                            </div>
                        </div>
                    </div>
                </div>
                <DashboardFooter />
            </>
        );
    }

    if (error || !serviceRequest) {
        return (
            <>
                <DashboardHeader />
                <SideMenu />
                <div className={"vertical-overlay"} />
                <div className={"main-content"}>
                    <div className={"page-content"}>
                        <div className={"container-fluid"}>
                            <div className="mb-3">
                                <button 
                                    className="btn btn-outline-secondary"
                                    onClick={() => window.history.back()}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                                    Back
                                </button>
                            </div>
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faFileAlt} size="3x" className="mb-3 text-danger" />
                                <h5>Error Loading Service Request</h5>
                                <p className="text-muted">{error || 'Service request not found'}</p>
                                <button 
                                    className="btn btn-primary"
                                    onClick={() => navigate('/institution/services/service-requests')}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                                    Back to Service Requests
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <DashboardFooter />
            </>
        );
    }

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"} />
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className={"container-fluid"}>
                        <div className="mb-3">
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={() => window.history.back()}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                                Back
                            </button>
                        </div>
                        
                        {/* Header */}
                        <div className="row mb-4 align-items-center">
                            <div className="col-auto">
                                <button 
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate('/institution/services/service-requests')}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                                    Back
                                </button>
                            </div>
                            <div className="col">
                                <h2 className="fw-bold mb-0">
                                    <FontAwesomeIcon 
                                        icon={getRequestIcon(serviceRequest.title, serviceRequest.engagement_type)} 
                                        className="me-2 text-primary" 
                                    />
                                    {serviceRequest.title}
                                </h2>
                                <div className="text-muted">Service Request Details</div>
                            </div>
                            <div className="col-auto">
                                {userRole === 'creator' && (
                                    <div className="btn-group">
                                        <button className="btn btn-outline-primary">
                                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                                            Edit
                                        </button>
                                        <button className="btn btn-outline-success">
                                            <FontAwesomeIcon icon={faCheck} className="me-2" />
                                            Approve
                                        </button>
                                        <button className="btn btn-outline-danger">
                                            <FontAwesomeIcon icon={faTimes} className="me-2" />
                                            Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="row">
                            {/* Main Content */}
                            <div className="col-lg-8">
                                {/* Service Request Details */}
                                <div className="card mb-4">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                            Request Information
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label fw-bold">Description</label>
                                                <p className="mb-0">{serviceRequest.description}</p>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label fw-bold">Request Type</label>
                                                <p className="mb-0">
                                                    <span className={`badge bg-${serviceRequest.request_type === 'direct' ? 'primary' : 'info'}`}>
                                                        {serviceRequest.request_type === 'direct' ? 'Direct Request' : 'Open Request'}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label fw-bold">Engagement Type</label>
                                                <p className="mb-0">{serviceRequest.engagement_type}</p>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label fw-bold">Response Time</label>
                                                <p className="mb-0">{serviceRequest.response_time} hours</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Required Medical Services */}
                                {serviceRequest.required_services && serviceRequest.required_services.length > 0 && (
                                    <div className="card mb-4">
                                        <div className="card-header">
                                            <h5 className="mb-0">
                                                <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                                                Required Medical Services
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                {serviceRequest.required_services.map((service, index) => (
                                                    <div key={service.id} className="col-md-6 mb-3">
                                                        <div className="border rounded p-3">
                                                            <h6 className="mb-1">{service.name}</h6>
                                                            <p className="text-muted mb-1">{service.title}</p>
                                                            <small className="text-muted">{service.subtitle}</small>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Contract Information */}
                                {serviceRequest.service_contract_id && (
                                    <div className="card mb-4">
                                        <div className="card-header">
                                            <h5 className="mb-0">
                                                <FontAwesomeIcon icon={faFileContract} className="me-2" />
                                                Contract Information
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Contract Name</label>
                                                    <p className="mb-0">{serviceRequest.service_contract_name || 'N/A'}</p>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Contract Duration</label>
                                                    <p className="mb-0">{serviceRequest.contract_duration || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Contract Term</label>
                                                    <p className="mb-0">{serviceRequest.contract_term || 'N/A'}</p>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Renewable</label>
                                                    <p className="mb-0">
                                                        <span className={`badge bg-${serviceRequest.renewable ? 'success' : 'secondary'}`}>
                                                            {serviceRequest.renewable ? 'Yes' : 'No'}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Patient Information */}
                                {serviceRequest.patient_name && (
                                    <div className="card mb-4">
                                        <div className="card-header">
                                            <h5 className="mb-0">
                                                <FontAwesomeIcon icon={faUser} className="me-2" />
                                                Patient Information
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Patient Name</label>
                                                    <p className="mb-0">{serviceRequest.patient_name}</p>
                                                </div>
                                                {serviceRequest.patient_phone && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-bold">Phone</label>
                                                        <p className="mb-0">
                                                            <FontAwesomeIcon icon={faPhone} className="me-2" />
                                                            {serviceRequest.patient_phone}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            {serviceRequest.patient_email && (
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">Email</label>
                                                    <p className="mb-0">
                                                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                                                        {serviceRequest.patient_email}
                                                    </p>
                                                </div>
                                            )}
                                            
                                            {/* Patient Consent Information */}
                                            <div className="mt-3 p-3 bg-light rounded">
                                                <h6 className="mb-2">Patient Consent & Information Sharing</h6>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <p className="mb-1">
                                                            <strong>Consent Given:</strong> 
                                                            <span className={`badge bg-${serviceRequest.patient_consent_given ? 'success' : 'warning'} ms-2`}>
                                                                {serviceRequest.patient_consent_given ? 'Yes' : 'No'}
                                                            </span>
                                                        </p>
                                                        {serviceRequest.patient_consent_date && (
                                                            <p className="mb-1">
                                                                <strong>Consent Date:</strong> {new Date(serviceRequest.patient_consent_date).toLocaleDateString()}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="row">
                                                            <div className="col-6">
                                                                <small className="d-block">
                                                                    <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                    Demographics: {serviceRequest.share_patient_demographics ? '✓' : '✗'}
                                                                </small>
                                                                <small className="d-block">
                                                                    <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                    Medical History: {serviceRequest.share_medical_history ? '✓' : '✗'}
                                                                </small>
                                                            </div>
                                                            <div className="col-6">
                                                                <small className="d-block">
                                                                    <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                    Lab Results: {serviceRequest.share_lab_results ? '✓' : '✗'}
                                                                </small>
                                                                <small className="d-block">
                                                                    <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                    Imaging: {serviceRequest.share_imaging ? '✓' : '✗'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Creator/Clinic Information */}
                                {serviceRequest.creator_name && (
                                    <div className="card mb-4">
                                        <div className="card-header">
                                            <h5 className="mb-0">
                                                <FontAwesomeIcon icon={faBuilding} className="me-2" />
                                                Requesting Clinic Information
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-bold">Clinic Name</label>
                                                    <p className="mb-0">{serviceRequest.creator_name}</p>
                                                </div>
                                                {serviceRequest.creator_phone && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label fw-bold">Phone</label>
                                                        <p className="mb-0">
                                                            <FontAwesomeIcon icon={faPhone} className="me-2" />
                                                            {serviceRequest.creator_phone}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            {serviceRequest.creator_address && (
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">Address</label>
                                                    <p className="mb-0">
                                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                                                        {serviceRequest.creator_address}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                                                 {/* Direct Partners (if applicable) */}
                                 {serviceRequest.request_type === 'direct' && serviceRequest.direct_partners && serviceRequest.direct_partners.length > 0 && (
                                     <div className="card mb-4">
                                         <div className="card-header">
                                             <h5 className="mb-0">
                                                 <FontAwesomeIcon icon={faUsers} className="me-2" />
                                                 Direct Partners
                                             </h5>
                                         </div>
                                         <div className="card-body">
                                             <div className="row">
                                                 {serviceRequest.direct_partners.map((partner, index) => (
                                                     <div key={partner.id} className="col-md-6 mb-3">
                                                         <div className="border rounded p-3">
                                                             <h6 className="mb-1">{partner.name}</h6>
                                                             <small className="text-muted">{partner.address}</small>
                                                         </div>
                                                     </div>
                                                 ))}
                                             </div>
                                         </div>
                                     </div>
                                 )}

                                 {/* Patient Consent & Information Sharing Section */}
                                 <div className="card mb-4">
                                     <div className="card-header">
                                         <h5 className="mb-0">
                                             <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                                             Patient Consent & Information Sharing
                                         </h5>
                                     </div>
                                     <div className="card-body">
                                         <div className="row">
                                             <div className="col-md-6">
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Consent Status</label>
                                                     <div>
                                                         <span className={`badge bg-${serviceRequest.patient_consent_given ? 'success' : 'warning'} fs-6`}>
                                                             {serviceRequest.patient_consent_given ? 'Consent Given' : 'Consent Pending'}
                                                         </span>
                                                     </div>
                                                     {serviceRequest.patient_consent_date && (
                                                         <small className="text-muted d-block mt-1">
                                                             Consent Date: {new Date(serviceRequest.patient_consent_date).toLocaleDateString()}
                                                         </small>
                                                     )}
                                                     {serviceRequest.patient_consent_notes && (
                                                         <small className="text-muted d-block mt-1">
                                                             Notes: {serviceRequest.patient_consent_notes}
                                                         </small>
                                                     )}
                                                 </div>
                                             </div>
                                             <div className="col-md-6">
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Information Sharing Preferences</label>
                                                     <div className="row">
                                                         <div className="col-6">
                                                             <small className="d-block mb-1">
                                                                 <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                 Demographics: {serviceRequest.share_patient_demographics ? '✓' : '✗'}
                                                             </small>
                                                             <small className="d-block mb-1">
                                                                 <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                 Medical History: {serviceRequest.share_medical_history ? '✓' : '✗'}
                                                             </small>
                                                             <small className="d-block mb-1">
                                                                 <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                 Current Medications: {serviceRequest.share_current_medications ? '✓' : '✗'}
                                                             </small>
                                                         </div>
                                                         <div className="col-6">
                                                             <small className="d-block mb-1">
                                                                 <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                 Lab Results: {serviceRequest.share_lab_results ? '✓' : '✗'}
                                                             </small>
                                                             <small className="d-block mb-1">
                                                                 <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                 Imaging: {serviceRequest.share_imaging ? '✓' : '✗'}
                                                             </small>
                                                             <small className="d-block mb-1">
                                                                 <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                                 Notes: {serviceRequest.share_notes ? '✓' : '✗'}
                                                             </small>
                                                         </div>
                                                     </div>
                                                 </div>
                                             </div>
                                         </div>
                                         
                                         {/* Partner Consent Checkboxes */}
                                         {userRole === 'partner' && (
                                             <div className="mt-3 p-3 bg-light rounded">
                                                 <h6 className="mb-3">Partner Consent Agreement</h6>
                                                 <p className="text-muted small mb-3">
                                                     Please review and check all segments you understand and agree to before placing a counter offer:
                                                 </p>
                                                 <div className="row">
                                                     <div className="col-md-6">
                                                         <div className="form-check mb-2">
                                                             <input 
                                                                 className="form-check-input" 
                                                                 type="checkbox" 
                                                                 id="consent-demographics"
                                                                 checked={consentCheckboxes.demographics}
                                                                 onChange={(e) => handleConsentChange('demographics', e.target.checked)}
                                                             />
                                                             <label className="form-check-label" htmlFor="consent-demographics">
                                                                 Patient Demographics
                                                             </label>
                                                         </div>
                                                         <div className="form-check mb-2">
                                                             <input 
                                                                 className="form-check-input" 
                                                                 type="checkbox" 
                                                                 id="consent-medical-history"
                                                                 checked={consentCheckboxes.medical_history}
                                                                 onChange={(e) => handleConsentChange('medical_history', e.target.checked)}
                                                             />
                                                             <label className="form-check-label" htmlFor="consent-medical-history">
                                                                 Medical History
                                                             </label>
                                                         </div>
                                                         <div className="form-check mb-2">
                                                             <input 
                                                                 className="form-check-input" 
                                                                 type="checkbox" 
                                                                 id="consent-medications"
                                                                 checked={consentCheckboxes.medications}
                                                                 onChange={(e) => handleConsentChange('medications', e.target.checked)}
                                                             />
                                                             <label className="form-check-label" htmlFor="consent-medications">
                                                                 Current Medications
                                                             </label>
                                                         </div>
                                                     </div>
                                                     <div className="col-md-6">
                                                         <div className="form-check mb-2">
                                                             <input 
                                                                 className="form-check-input" 
                                                                 type="checkbox" 
                                                                 id="consent-lab-results"
                                                                 checked={consentCheckboxes.lab_results}
                                                                 onChange={(e) => handleConsentChange('lab_results', e.target.checked)}
                                                             />
                                                             <label className="form-check-label" htmlFor="consent-lab-results">
                                                                 Lab Results
                                                             </label>
                                                         </div>
                                                         <div className="form-check mb-2">
                                                             <input 
                                                                 className="form-check-input" 
                                                                 type="checkbox" 
                                                                 id="consent-imaging"
                                                                 checked={consentCheckboxes.imaging}
                                                                 onChange={(e) => handleConsentChange('imaging', e.target.checked)}
                                                             />
                                                             <label className="form-check-label" htmlFor="consent-imaging">
                                                                 Imaging
                                                             </label>
                                                         </div>
                                                         <div className="form-check mb-2">
                                                             <input 
                                                                 className="form-check-input" 
                                                                 type="checkbox" 
                                                                 id="consent-notes"
                                                                 checked={consentCheckboxes.notes}
                                                                 onChange={(e) => handleConsentChange('notes', e.target.checked)}
                                                             />
                                                             <label className="form-check-label" htmlFor="consent-notes">
                                                                 Medical Notes
                                                             </label>
                                                         </div>
                                                     </div>
                                                 </div>
                                                 
                                                 <div className="mt-3 p-3 bg-info bg-opacity-10 rounded">
                                                     <div className="d-flex align-items-center">
                                                         <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
                                                         <div>
                                                             <strong>All Segments Checked:</strong> 
                                                             <span className={`badge bg-${allSegmentsChecked ? 'success' : 'warning'} ms-2`}>
                                                                 {allSegmentsChecked ? 'Ready to Proceed' : 'Pending'}
                                                             </span>
                                                         </div>
                                                     </div>
                                                     {allSegmentsChecked && (
                                                         <button 
                                                             className="btn btn-success btn-lg mt-3 w-100"
                                                             onClick={() => setShowApplyModal(true)}
                                                         >
                                                             <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                                             Apply to Service Request
                                                         </button>
                                                     )}
                                                 </div>
                                             </div>
                                         )}
                                     </div>
                                 </div>

                                {/* Contact Information */}
                                <div className="card mb-4">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <FontAwesomeIcon icon={faBuilding} className="me-2" />
                                            Contact Information
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <h6 className="mb-3">Request Creator</h6>
                                                <div className="mb-2">
                                                    <strong>Clinic:</strong> {serviceRequest.creator_name || 'N/A'}
                                                </div>
                                                {serviceRequest.creator_phone && (
                                                    <div className="mb-2">
                                                        <strong>Phone:</strong> 
                                                        <a href={`tel:${serviceRequest.creator_phone}`} className="ms-2">
                                                            {serviceRequest.creator_phone}
                                                        </a>
                                                    </div>
                                                )}
                                                {serviceRequest.creator_address && (
                                                    <div className="mb-2">
                                                        <strong>Address:</strong> {serviceRequest.creator_address}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="mb-3">Service Request Details</h6>
                                                <div className="mb-2">
                                                    <strong>Request Type:</strong> 
                                                    <span className={`badge ${serviceRequest.request_type === 'direct' ? 'bg-warning' : 'bg-success'} ms-2`}>
                                                        {serviceRequest.request_type === 'direct' ? 'Direct Request' : 'Open Request'}
                                                    </span>
                                                </div>
                                                {serviceRequest.request_type === 'direct' && serviceRequest.direct_partners && (
                                                    <div className="mb-2">
                                                        <strong>Direct Partners:</strong>
                                                        <div className="mt-1">
                                                            {serviceRequest.direct_partners.map((partner, index) => (
                                                                <span key={partner.id} className="badge bg-secondary me-1 mb-1">
                                                                    {partner.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {serviceRequest.required_services && serviceRequest.required_services.length > 0 && (
                                                    <div className="mb-2">
                                                        <strong>Required Services:</strong>
                                                        <div className="mt-1">
                                                            {serviceRequest.required_services.map((service, index) => (
                                                                <span key={service.id} className="badge bg-info me-1 mb-1">
                                                                    {service.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Contract Building & Management */}
                                {userRole === 'creator' && (
                                    <div className="card mb-4">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">
                                                <FontAwesomeIcon icon={faFileContract} className="me-2" />
                                                Contract Building & Management
                                            </h5>
                                            <button 
                                                className="btn btn-primary btn-sm"
                                                onClick={() => setShowContractBuilderModal(true)}
                                            >
                                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                Build Contract
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {serviceRequest.service_contract_id ? (
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <h6 className="mb-3">Current Contract</h6>
                                                        <div className="mb-2">
                                                            <strong>Contract ID:</strong> {serviceRequest.service_contract_id}
                                                        </div>
                                                        <div className="mb-2">
                                                            <strong>Contract Name:</strong> {serviceRequest.service_contract_name || 'N/A'}
                                                        </div>
                                                        <div className="mb-2">
                                                            <strong>Duration:</strong> {serviceRequest.contract_duration || 'N/A'}
                                                        </div>
                                                        <div className="mb-2">
                                                            <strong>Type:</strong> {serviceRequest.contract_term || 'N/A'}
                                                        </div>
                                                        <div className="mb-2">
                                                            <strong>Renewable:</strong> 
                                                            <span className={`badge ${serviceRequest.renewable ? 'bg-success' : 'bg-secondary'}`}>
                                                                {serviceRequest.renewable ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <h6 className="mb-3">Contract Management</h6>
                                                        <p className="text-muted mb-3">
                                                            Manage your contract details and upload supporting documents.
                                                        </p>
                                                        <button 
                                                            className="btn btn-outline-success btn-sm me-2"
                                                            onClick={() => setShowUploadContractFileModal(true)}
                                                        >
                                                            <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                                                            Upload Contract File
                                                        </button>
                                                        <button 
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={() => setShowContractBuilderModal(true)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                            Edit Contract
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <FontAwesomeIcon icon={faFileContract} size="3x" className="text-muted mb-3" />
                                                    <h6>No Contract Created Yet</h6>
                                                    <p className="text-muted">Click "Build Contract" to create a contract for this service request.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Service Request Documents */}
                                <div id="documents-section" className="card mb-4">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">
                                            <FontAwesomeIcon icon={faFileMedical} className="me-2" />
                                            Documents & Contracts
                                        </h5>
                                        <div className="btn-group btn-group-sm">
                                            <button 
                                                className={`btn btn-outline-primary ${viewMode === 'cards' ? 'active' : ''}`}
                                                onClick={() => setViewMode('cards')}
                                            >
                                                Cards
                                            </button>
                                            <button 
                                                className={`btn btn-outline-primary ${viewMode === 'list' ? 'active' : ''}`}
                                                onClick={() => setViewMode('list')}
                                            >
                                                List
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        {serviceRequest.service_request_documents && serviceRequest.service_request_documents.length > 0 ? (
                                            viewMode === 'cards' ? (
                                                <div className="row">
                                                    {serviceRequest.service_request_documents.map((doc, index) => (
                                                        <div key={doc.id} className="col-md-6 mb-3">
                                                            <div className="border rounded p-3">
                                                                <div className="d-flex align-items-center">
                                                                    <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="mb-1">{doc.file_description}</h6>
                                                                        <small className="text-muted">
                                                                            Added: {new Date(doc.created).toLocaleDateString()}
                                                                        </small>
                                                                    </div>
                                                                    <button className="btn btn-sm btn-outline-primary">
                                                                        <FontAwesomeIcon icon={faEye} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>Document</th>
                                                                <th>Description</th>
                                                                <th>Added</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {serviceRequest.service_request_documents.map((doc, index) => (
                                                                <tr key={doc.id}>
                                                                    <td>
                                                                        <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                                                                        Document {index + 1}
                                                                    </td>
                                                                    <td>{doc.file_description}</td>
                                                                    <td>{new Date(doc.created).toLocaleDateString()}</td>
                                                                    <td>
                                                                        <button className="btn btn-sm btn-outline-primary">
                                                                            <FontAwesomeIcon icon={faEye} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )
                                        ) : (
                                            <div className="text-center py-4">
                                                <FontAwesomeIcon icon={faFileMedical} size="3x" className="text-muted mb-3" />
                                                <h6>No Documents Available</h6>
                                                <p className="text-muted">This service request doesn't have any additional documents yet.</p>
                                            </div>
                                        )}
                                        
                                        {/* Contract Information */}
                                        {serviceRequest.service_contract_id && (
                                            <div className="mt-4 p-3 bg-light rounded">
                                                <h6 className="mb-3">
                                                    <FontAwesomeIcon icon={faFileContract} className="me-2" />
                                                    Contract Details
                                                </h6>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <small className="text-muted d-block">Contract Name</small>
                                                        <strong>{serviceRequest.service_contract_name || 'N/A'}</strong>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <small className="text-muted d-block">Duration</small>
                                                        <strong>{serviceRequest.contract_duration || 'N/A'}</strong>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    <div className="col-md-6">
                                                        <small className="text-muted d-block">Contract Terms</small>
                                                        <strong>{serviceRequest.contract_term || 'N/A'}</strong>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <small className="text-muted d-block">Renewable</small>
                                                        <strong>{serviceRequest.renewable ? 'Yes' : 'No'}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Applications */}
                                {serviceRequest.applications && serviceRequest.applications.length > 0 && (
                                    <div id="applications-section" className="card mb-4">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">
                                                <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                                Partner Applications ({serviceRequest.applications.length})
                                            </h5>
                                            <div className="btn-group btn-group-sm">
                                                <button 
                                                    className={`btn btn-outline-primary ${viewMode === 'cards' ? 'active' : ''}`}
                                                    onClick={() => setViewMode('cards')}
                                                >
                                                    Cards
                                                </button>
                                                <button 
                                                    className={`btn btn-outline-primary ${viewMode === 'list' ? 'active' : ''}`}
                                                    onClick={() => setViewMode('list')}
                                                >
                                                    List
                                                </button>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            {serviceRequest.applications.map((app, index) => (
                                                <div key={app.id} className="border rounded p-3 mb-3">
                                                    <div className="row align-items-center">
                                                        <div className="col-md-4">
                                                            <h6 className="mb-1">{app.partner_applying_name}</h6>
                                                            <span className={`badge bg-${getStatusColor(app.status)}`}>
                                                                {app.status.replace('_', ' ').toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <small className="d-block">
                                                                <strong>Rate:</strong> ${app.partner_minimum} - ${app.partner_maximum}
                                                            </small>
                                                            <small className="d-block">
                                                                <strong>Pay Mode:</strong> {getPayModeDisplay(app.pay_mode)}
                                                            </small>
                                                            <small className="d-block">
                                                                <strong>Patient Flow:</strong> {getPatientFlowDisplay(app.patient_flow, app.patient_flow_rate)}
                                                            </small>
                                                        </div>
                                                        <div className="col-md-4 text-end">
                                                            <button className="btn btn-sm btn-outline-primary me-2">
                                                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                                                View
                                                            </button>
                                                            {userRole === 'creator' && app.status === 'submitted' && (
                                                                <>
                                                                    <button 
                                                                        className="btn btn-sm btn-outline-success me-2"
                                                                        onClick={() => handleAcceptApplication(app.id)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                                        Accept
                                                                    </button>
                                                                    <button 
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => handleRejectApplication(app.id)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                                                                        Reject
                                                                    </button>
                                                                </>
                                                            )}
                                                            {userRole === 'partner' && app.status === 'accepted' && (
                                                                <button 
                                                                    className="btn btn-sm btn-outline-success"
                                                                    onClick={() => handleConvertToCollaboration(app.id)}
                                                                >
                                                                    <FontAwesomeIcon icon={faHandshakeIcon} className="me-1" />
                                                                    Convert to Collaboration
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Sidebar */}
                            <div className="col-lg-4">
                                {/* Status & Priority */}
                                <div className="card mb-4">
                                    <div className="card-header">
                                        <h6 className="mb-0">Status & Priority</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Status</label>
                                            <div>
                                                <span className={`badge bg-${getStatusColor(serviceRequest.status)} fs-6`}>
                                                    {serviceRequest.status.replace('_', ' ').toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Priority</label>
                                            <div>
                                                <span className={`badge bg-${getPriorityColor(serviceRequest.priority)} fs-6`}>
                                                    {serviceRequest.priority.toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                                                 {/* Timeline & Activity Tracking */}
                                 <div className="card mb-4">
                                     <div className="card-header d-flex justify-content-between align-items-center">
                                         <h6 className="mb-0">
                                             <FontAwesomeIcon icon={faClockIcon} className="me-2" />
                                             Timeline & Activity Tracking
                                         </h6>
                                         <div className="btn-group btn-group-sm">
                                             <button 
                                                 className={`btn btn-outline-primary ${viewMode === 'cards' ? 'active' : ''}`}
                                                 onClick={() => setViewMode('cards')}
                                             >
                                                 Cards
                                             </button>
                                             <button 
                                                 className={`btn btn-outline-primary ${viewMode === 'list' ? 'active' : ''}`}
                                                 onClick={() => setViewMode('list')}
                                             >
                                                 List
                                             </button>
                                             </div>
                                     </div>
                                     <div className="card-body">
                                         <div className="row">
                                             <div className="col-md-6">
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Created</label>
                                                     <p className="mb-0">
                                                         <FontAwesomeIcon icon={faCalendar} className="me-2" />
                                                         {formatDate(serviceRequest.created)}
                                                     </p>
                                                 </div>
                                                 {serviceRequest.start_date && (
                                                     <div className="mb-3">
                                                         <label className="form-label fw-bold">Start Date</label>
                                                         <p className="mb-0">
                                                             <FontAwesomeIcon icon={faCalendar} className="me-2" />
                                                             {formatDate(serviceRequest.start_date)}
                                                         </p>
                                                     </div>
                                                 )}
                                                 {serviceRequest.end_date && (
                                                     <div className="mb-3">
                                                         <label className="form-label fw-bold">End Date</label>
                                                         <p className="mb-0">
                                                             <FontAwesomeIcon icon={faCalendar} className="me-2" />
                                                             {formatDate(serviceRequest.end_date)}
                                                         </p>
                                                     </div>
                                                 )}
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Last Updated</label>
                                                     <p className="mb-0">
                                                         <FontAwesomeIcon icon={faClock} className="me-2" />
                                                         {formatDate(serviceRequest.updated)}
                                                     </p>
                                                 </div>
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Response Time</label>
                                                     <p className="mb-0">
                                                         <FontAwesomeIcon icon={faClockIcon} className="me-2" />
                                                         {serviceRequest.response_time} hours
                                                         <small className="text-muted ms-2">
                                                             ({Math.ceil(serviceRequest.response_time / 24)} days)
                                                         </small>
                                                     </p>
                                                 </div>
                                             </div>
                                             <div className="col-md-6">
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Status History</label>
                                                     
                                                     {/* Status Summary */}
                                                     <div className="mb-3 p-3 bg-light rounded">
                                                         <div className="row text-center">
                                                             <div className="col-4">
                                                                 <div className="h5 mb-1 text-primary">
                                                                     {serviceRequest.status.replace('_', ' ').toUpperCase()}
                                                                 </div>
                                                                 <small className="text-muted">Current Status</small>
                                                             </div>
                                                             <div className="col-4">
                                                                 <div className="h5 mb-1 text-info">
                                                                     {serviceRequest.applications ? serviceRequest.applications.length : 0}
                                                                 </div>
                                                                 <small className="text-muted">Applications</small>
                                                             </div>
                                                             <div className="col-4">
                                                                 <div className="h5 mb-1 text-success">
                                                                     {serviceRequest.response_time}
                                                                 </div>
                                                                 <small className="text-muted">Response Time (hrs)</small>
                                                             </div>
                                                         </div>
                                                     </div>
                                                     
                                                     <div className="timeline">
                                                         <div className="timeline-item">
                                                             <div className="timeline-marker bg-success"></div>
                                                             <div className="timeline-content">
                                                                 <small className="text-muted">
                                                                     {formatDate(serviceRequest.created)}
                                                                 </small>
                                                                 <p className="mb-0">Service request created</p>
                                                             </div>
                                                         </div>
                                                         {serviceRequest.status !== 'draft' && (
                                                             <div className="timeline-item">
                                                                 <div className="timeline-marker bg-info"></div>
                                                                 <div className="timeline-content">
                                                                     <small className="text-muted">
                                                                         {serviceRequest.status === 'pending' ? 'Published' : 'Status Updated'}
                                                                     </small>
                                                                     <p className="mb-0">
                                                                         {serviceRequest.status === 'pending' 
                                                                             ? 'Request published to partners' 
                                                                             : `Status changed to ${serviceRequest.status.replace('_', ' ')}`
                                                                         }
                                                                     </p>
                                                                 </div>
                                                             </div>
                                                         )}
                                                         {serviceRequest.applications && serviceRequest.applications.length > 0 && (
                                                             <div className="timeline-item">
                                                                 <div className="timeline-marker bg-warning"></div>
                                                                 <div className="timeline-content">
                                                                     <small className="text-muted">Applications Received</small>
                                                                     <p className="mb-0">
                                                                         {serviceRequest.applications.length} partner(s) applied
                                                                     </p>
                                                                 </div>
                                                             </div>
                                                         )}
                                                         {serviceRequest.status === 'negotiating' && (
                                                             <div className="timeline-item">
                                                                 <div className="timeline-marker bg-primary"></div>
                                                                 <div className="timeline-content">
                                                                     <small className="text-muted">Negotiating</small>
                                                                     <p className="mb-0">Terms under negotiation</p>
                                                                 </div>
                                                             </div>
                                                         )}
                                                         {serviceRequest.status === 'agreed' && (
                                                             <div className="timeline-item">
                                                                 <div className="timeline-marker bg-success"></div>
                                                                 <div className="timeline-content">
                                                                     <small className="text-muted">Agreed</small>
                                                                     <p className="mb-0">Final agreement reached</p>
                                                                 </div>
                                                             </div>
                                                         )}
                                                         {serviceRequest.status === 'upgraded' && (
                                                             <div className="timeline-item">
                                                                 <div className="timeline-marker bg-success"></div>
                                                                 <div className="timeline-content">
                                                                     <small className="text-muted">Upgraded</small>
                                                                     <p className="mb-0">Converted to collaboration</p>
                                                                 </div>
                                                             </div>
                                                         )}
                                                         {serviceRequest.status === 'rejected' && (
                                                             <div className="timeline-item">
                                                                 <div className="timeline-marker bg-danger"></div>
                                                                 <div className="timeline-content">
                                                                     <small className="text-muted">Rejected</small>
                                                                     <p className="mb-0">Request was rejected</p>
                                                                 </div>
                                                             </div>
                                                         )}
                                                         {serviceRequest.status === 'cancelled' && (
                                                             <div className="timeline-item">
                                                                 <div className="timeline-marker bg-secondary"></div>
                                                                 <div className="timeline-content">
                                                                     <small className="text-muted">Cancelled</small>
                                                                     <p className="mb-0">Request was cancelled</p>
                                                                 </div>
                                                             </div>
                                                         )}
                                                     </div>
                                                 </div>
                                             </div>
                                         </div>
                                     </div>
                                 </div>

                                 {/* Payment Information */}
                                 <div className="card mb-4">
                                     <div className="card-header">
                                         <h6 className="mb-0">
                                             <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                                             Payment Details
                                         </h6>
                                     </div>
                                     <div className="card-body">
                                         <div className="row">
                                             <div className="col-md-6">
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Payment Mode</label>
                                                     <p className="mb-0">
                                                         <span className="badge bg-info">
                                                             {getPayModeDisplay(serviceRequest.pay_mode)}
                                                         </span>
                                                     </p>
                                                 </div>
                                                 {serviceRequest.min_pay && (
                                                     <div className="mb-3">
                                                         <label className="form-label fw-bold">Minimum Payment</label>
                                                         <p className="mb-0">
                                                             <FontAwesomeIcon icon={faMoneyBillWave} className="me-2 text-success" />
                                                             Ksh {serviceRequest.min_pay.toLocaleString()}
                                                         </p>
                                                     </div>
                                                 )}
                                                 {serviceRequest.max_pay && (
                                                     <div className="mb-3">
                                                         <label className="form-label fw-bold">Maximum Payment</label>
                                                         <p className="mb-0">
                                                             <FontAwesomeIcon icon={faMoneyBillWave} className="me-2 text-success" />
                                                             Ksh {serviceRequest.max_pay.toLocaleString()}
                                                         </p>
                                                     </div>
                                                 )}
                                             </div>
                                             <div className="col-md-6">
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Payment Range</label>
                                                     {serviceRequest.min_pay && serviceRequest.max_pay ? (
                                                         <>
                                                             <div className="progress mb-2" style={{ height: '20px' }}>
                                                                 <div 
                                                                     className="progress-bar bg-success" 
                                                                     style={{ width: '100%' }}
                                                                 ></div>
                                                             </div>
                                                             <small className="text-muted">
                                                                 Range: Ksh {serviceRequest.min_pay.toLocaleString()} - Ksh {serviceRequest.max_pay.toLocaleString()}
                                                             </small>
                                                         </>
                                                     ) : (
                                                         <p className="text-muted">No payment range specified</p>
                                                     )}
                                                 </div>
                                                 <div className="mb-3">
                                                     <label className="form-label fw-bold">Contract Terms</label>
                                                     <p className="mb-0">
                                                         <small className="text-muted">
                                                             {serviceRequest.contract_duration || 'Not specified'}
                                                         </small>
                                                     </p>
                                                 </div>
                                             </div>
                                         </div>
                                     </div>
                                 </div>

                                 {/* Quick Actions */}
                                 <div className="card mb-4">
                                     <div className="card-header">
                                         <h6 className="mb-0">Quick Actions</h6>
                                     </div>
                                     <div className="card-body">
                                         <div className="d-grid gap-2">
                                             {userRole === 'creator' && (
                                                 <>
                                                     <button 
                                                         className="btn btn-outline-primary btn-sm"
                                                         onClick={() => navigate(`/institution/services/edit-request/${serviceRequest?.id}`)}
                                                     >
                                                         <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                         Edit Request
                                                     </button>
                                                     <button 
                                                         className="btn btn-outline-success btn-sm"
                                                         onClick={() => document.getElementById('applications-section')?.scrollIntoView({ behavior: 'smooth' })}
                                                     >
                                                         <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                                         View Applications
                                                     </button>
                                                     <button 
                                                         className="btn btn-outline-info btn-sm"
                                                         onClick={() => document.getElementById('documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                                                     >
                                                         <FontAwesomeIcon icon={faFileContract} className="me-2" />
                                                         Manage Contracts
                                                     </button>
                                                     <button 
                                                         className="btn btn-outline-warning btn-sm"
                                                         onClick={() => setShowInvitePartnersModal(true)}
                                                     >
                                                         <FontAwesomeIcon icon={faUsers} className="me-2" />
                                                         Invite Partners
                                                     </button>
                                                 </>
                                             )}
                                             {userRole === 'partner' && (
                                                 <>
                                                     <button 
                                                         className="btn btn-outline-primary btn-sm"
                                                         onClick={() => setShowApplyModal(true)}
                                                         disabled={!allSegmentsChecked}
                                                     >
                                                         <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                                         Apply to Request
                                                     </button>
                                                     <button 
                                                         className="btn btn-outline-info btn-sm"
                                                         onClick={() => document.getElementById('documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                                                     >
                                                         <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                                         View Documents
                                                     </button>
                                                     <button 
                                                         className="btn btn-outline-success btn-sm"
                                                         onClick={() => setShowAcceptTermsModal(true)}
                                                     >
                                                         <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                                         Accept Terms
                                                     </button>
                                                 </>
                                             )}
                                         </div>
                                     </div>
                                 </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <DashboardFooter />

            {/* Apply to Service Request Modal */}
            {showApplyModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                    Apply to Service Request
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowApplyModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    <strong>Important:</strong> Please provide your proposed terms and collaboration details. This will be sent as a counter offer to the request creator.
                                </div>
                                
                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Proposed Rate (Ksh) *</label>
                                        <input 
                                            type="number" 
                                            className="form-control"
                                            value={applyFormData.proposed_rate}
                                            onChange={(e) => setApplyFormData(prev => ({ ...prev, proposed_rate: e.target.value }))}
                                            placeholder="Enter your proposed rate"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Proposed Timeline *</label>
                                        <input 
                                            type="text" 
                                            className="form-control"
                                            value={applyFormData.proposed_timeline}
                                            onChange={(e) => setApplyFormData(prev => ({ ...prev, proposed_timeline: e.target.value }))}
                                            placeholder="e.g., 2 weeks, 1 month"
                                        />
                                    </div>
                                </div>
                                
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Proposed Terms *</label>
                                        <textarea 
                                            className="form-control" 
                                            rows={3}
                                            value={applyFormData.proposed_terms}
                                            onChange={(e) => setApplyFormData(prev => ({ ...prev, proposed_terms: e.target.value }))}
                                            placeholder="Describe your proposed terms, conditions, and approach..."
                                        ></textarea>
                                    </div>
                                </div>
                                
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Collaboration Details</label>
                                        <textarea 
                                            className="form-control" 
                                            rows={3}
                                            value={applyFormData.collaboration_details}
                                            onChange={(e) => setApplyFormData(prev => ({ ...prev, collaboration_details: e.target.value }))}
                                            placeholder="Describe how you plan to collaborate, your team structure, methodology..."
                                        ></textarea>
                                    </div>
                                </div>
                                
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Counter Offer Details</label>
                                        <textarea 
                                            className="form-control" 
                                            rows={3}
                                            value={applyFormData.counter_offer}
                                            onChange={(e) => setApplyFormData(prev => ({ ...prev, counter_offer: e.target.value }))}
                                            placeholder="Any specific counter offers or modifications to the original request..."
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowApplyModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-success"
                                    onClick={handleApplyToRequest}
                                    disabled={!applyFormData.proposed_rate || !applyFormData.proposed_timeline || !applyFormData.proposed_terms}
                                >
                                    <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                    Submit Application
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Invite Partners Modal */}
            {showInvitePartnersModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                                    Invite Partners
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowInvitePartnersModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="text-muted">This feature will allow you to invite specific partners to your service request.</p>
                                <div className="alert alert-info">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    <strong>Coming Soon:</strong> Partner invitation functionality will be available in the next update.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowInvitePartnersModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contract Builder Modal */}
            {showContractBuilderModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faFileContract} className="me-2" />
                                    Build/Edit Contract
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowContractBuilderModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    <strong>Contract Builder:</strong> Build or modify the contract for this service request. You can customize terms, duration, and other contract details.
                                </div>
                                
                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Contract Name</label>
                                        <input 
                                            type="text" 
                                            className="form-control"
                                            placeholder="Enter contract name"
                                            defaultValue={serviceRequest.service_contract_name || ''}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Contract Duration</label>
                                        <select className="form-select">
                                            <option value="long_term">Long Term Contract</option>
                                            <option value="medium_term">Average Term Contract</option>
                                            <option value="short_term">Short Term Contract</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="row mt-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Contract Type</label>
                                        <select className="form-select">
                                            <option value="permanent">Permanent Engagement</option>
                                            <option value="term">Term Engagement</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Renewable</label>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="renewable" />
                                            <label className="form-check-label" htmlFor="renewable">
                                                Contract is renewable
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Contract Terms</label>
                                        <textarea 
                                            className="form-control" 
                                            rows={4}
                                            placeholder="Enter detailed contract terms and conditions..."
                                        ></textarea>
                                    </div>
                                </div>
                                
                                <div className="row mt-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Start Date</label>
                                        <input 
                                            type="date" 
                                            className="form-control"
                                            defaultValue={serviceRequest.start_date ? serviceRequest.start_date.split('T')[0] : ''}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">End Date</label>
                                        <input 
                                            type="date" 
                                            className="form-control"
                                            defaultValue={serviceRequest.end_date ? serviceRequest.end_date.split('T')[0] : ''}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowContractBuilderModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={() => {
                                        // Handle contract save
                                        alert('Contract saved successfully!');
                                        setShowContractBuilderModal(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileContract} className="me-2" />
                                    Save Contract
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Contract File Modal */}
            {showUploadContractFileModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                                    Upload Contract File
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowUploadContractFileModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">File Description</label>
                                    <input 
                                        type="text" 
                                        className="form-control"
                                        placeholder="Describe what this file contains..."
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Select File</label>
                                    <input 
                                        type="file" 
                                        className="form-control"
                                        accept=".pdf,.doc,.docx,.txt"
                                    />
                                    <small className="text-muted">
                                        Supported formats: PDF, DOC, DOCX, TXT (Max size: 10MB)
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowUploadContractFileModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-success"
                                    onClick={() => {
                                        // Handle file upload
                                        alert('File uploaded successfully!');
                                        setShowUploadContractFileModal(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                                    Upload File
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Accept Terms Modal */}
            {showAcceptTermsModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                    Accept Terms
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowAcceptTermsModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="text-muted">This feature will allow you to accept the terms of the service request.</p>
                                <div className="alert alert-info">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    <strong>Coming Soon:</strong> Terms acceptance functionality will be available in the next update.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowAcceptTermsModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Negotiation Modal */}
            {showNegotiationModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Place Counter Offer</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowNegotiationModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Message</label>
                                    <textarea 
                                        className="form-control" 
                                        rows={3}
                                        value={negotiationMessage}
                                        onChange={(e) => setNegotiationMessage(e.target.value)}
                                        placeholder="Enter your counter offer message..."
                                    ></textarea>
                                </div>
                                
                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="form-label">Proposed Rate (Ksh)</label>
                                        <input 
                                            type="number" 
                                            className="form-control"
                                            value={proposedTerms.rate}
                                            onChange={(e) => setProposedTerms(prev => ({ ...prev, rate: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Pay Mode</label>
                                        <select 
                                            className="form-select"
                                            value={proposedTerms.pay_mode}
                                            onChange={(e) => setProposedTerms(prev => ({ ...prev, pay_mode: e.target.value }))}
                                        >
                                            <option value="">Select Pay Mode</option>
                                            <option value="person">Per Person</option>
                                            <option value="request">Per Request</option>
                                            <option value="milestone">Per Milestone</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="row mt-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Patient Flow</label>
                                        <input 
                                            type="number" 
                                            className="form-control"
                                            value={proposedTerms.patient_flow}
                                            onChange={(e) => setProposedTerms(prev => ({ ...prev, patient_flow: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Flow Rate</label>
                                        <select 
                                            className="form-select"
                                            value={proposedTerms.patient_flow_rate}
                                            onChange={(e) => setProposedTerms(prev => ({ ...prev, patient_flow_rate: e.target.value }))}
                                        >
                                            <option value="">Select Rate</option>
                                            <option value="hourly">Per Hour</option>
                                            <option value="daily">Per Day</option>
                                            <option value="weekly">Per Week</option>
                                            <option value="monthly">Per Month</option>
                                            <option value="yearly">Per Year</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowNegotiationModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleCounterOffer}
                                    disabled={!negotiationMessage.trim()}
                                >
                                    Submit Counter Offer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Add CSS styles for timeline component
const timelineStyles = `
    .timeline {
        position: relative;
        padding-left: 30px;
    }
    
    .timeline-item {
        position: relative;
        margin-bottom: 20px;
    }
    
    .timeline-marker {
        position: absolute;
        left: -35px;
        top: 5px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-shadow: 0 0 0 3px #dee2e6;
    }
    
    .timeline-content {
        padding-left: 10px;
    }
    
    .timeline-content small {
        color: #6c757d;
        font-size: 0.875em;
    }
    
    .timeline-content p {
        margin: 0;
        font-weight: 500;
    }
`;

export default ServiceRequestDetails;
